import { type ReactNode, useRef, useState } from "react";
import { moneyShort } from "@/plan-logic/lib/format";
import "@/styles/v47-pages.css";

/** One line the crosshair reads: its name, colour, and its value at each of the chart's samples. */
export type XhSeries = { n: string; c: string; v: (number | undefined)[] };

/**
 * The prototype's crosshair (v44 `xhairSvg`): a dashed vertical rule that follows the pointer and
 * reads every line at that point in time, with a dot on each line and a readout above the plot.
 * One implementation for every line chart (Net Worth, Home, the Decision Hub's two-line charts).
 *
 * Wraps the chart's own svg: pass the plot frame (viewBox `W`×`H`, margins `L` `R` `T` `B`), the
 * samples' x positions in svg units (`xs`, ascending) with their labels (the year), each series'
 * values at those samples, and the y scale. The chart's marks go in `children`.
 */
export function CrosshairSvg({
  W,
  H,
  L,
  R,
  T,
  B,
  xs,
  labels,
  series,
  y,
  fmt = moneyShort,
  label,
  testId,
  className,
  children,
}: {
  W: number;
  H: number;
  L: number;
  R: number;
  T: number;
  B: number;
  xs: number[];
  labels: (string | number)[];
  series: XhSeries[];
  y: (v: number) => number;
  fmt?: (v: number) => string;
  label: string;
  testId?: string;
  className?: string;
  children: ReactNode;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ i: number; left: number } | null>(null);

  const move = (clientX: number | undefined) => {
    const r = svg.current?.getBoundingClientRect();
    if (!r?.width || !xs.length || clientX === undefined) return;
    const vx = ((clientX - r.left) / r.width) * W;
    let i = 0;
    xs.forEach((x, k) => {
      if (Math.abs(x - vx) < Math.abs(xs[i] - vx)) i = k;
    });
    const px = (xs[i] / W) * r.width;
    const half = (tip.current?.offsetWidth ?? 0) / 2;
    setAt({ i, left: Math.min(Math.max(px, half + 4), r.width - half - 4) });
  };

  const x = at ? xs[at.i] : 0;
  return (
    <div className="xhwrap" data-testid={testId}>
      <svg ref={svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} className={className}>
        {children}
        <g className="xhg" opacity={at ? 1 : 0} pointerEvents="none">
          <line
            className="xhl"
            x1={x.toFixed(1)}
            x2={x.toFixed(1)}
            y1={T}
            y2={H - B}
            stroke="var(--ink3)"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          {at &&
            series.map((s) => {
              const v = s.v[at.i];
              if (v === undefined || !Number.isFinite(v)) return null;
              return (
                <circle
                  key={s.n}
                  className="xhd"
                  cx={x.toFixed(1)}
                  cy={y(v).toFixed(1)}
                  r="4.5"
                  fill={s.c}
                  stroke="var(--card)"
                  strokeWidth="1.6"
                />
              );
            })}
        </g>
        <rect
          className="xhhit"
          x={L}
          y={T}
          width={Math.max(0, W - L - R).toFixed(1)}
          height={Math.max(0, H - B - T).toFixed(1)}
          fill="transparent"
          style={{ cursor: "crosshair" }}
          onMouseMove={(e) => move(e.clientX)}
          onMouseLeave={() => setAt(null)}
          onTouchStart={(e) => move(e.touches[0]?.clientX)}
          onTouchMove={(e) => move(e.touches[0]?.clientX)}
          data-testid={testId ? `${testId}-hit` : undefined}
        />
      </svg>
      <div
        ref={tip}
        className={`xhtip${at ? " on" : ""}`}
        aria-hidden="true"
        style={at ? { left: at.left } : undefined}
        data-testid={testId ? `${testId}-tip` : undefined}
      >
        {at && (
          <>
            <b>{labels[at.i]}</b>
            {series.map((s) => {
              const v = s.v[at.i];
              if (v === undefined || !Number.isFinite(v)) return null;
              return (
                <span className="xhs" key={s.n}>
                  <i style={{ background: s.c }} />
                  {s.n}
                  <em>{fmt(v)}</em>
                </span>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

/** A line's value at `x`, linearly between its points; undefined outside the line. */
export function valueAt(pts: { x: number; value: number }[], x: number): number | undefined {
  if (!pts.length || x < pts[0].x - 1e-9 || x > pts[pts.length - 1].x + 1e-9) return undefined;
  for (let k = 0; k < pts.length; k++) {
    const p = pts[k];
    if (Math.abs(p.x - x) < 1e-9) return p.value;
    const q = pts[k + 1];
    if (q && x > p.x && x < q.x) return p.value + ((x - p.x) / (q.x - p.x)) * (q.value - p.value);
  }
  return undefined;
}
